"use client"

import { Text } from "@/elements"
import { graphql } from "@/gql"
import { useSession } from "@/hooks/useSession"
import { useQuery } from "@apollo/client"
import { ChevronLeftIcon } from "@heroicons/react/24/outline"
import clsx from "clsx"
import Link from "next/link"
import { useRouter } from "next-nprogress-bar"
import { useMemo, useState } from "react"
import { useChannelStateContext } from "stream-chat-react"
import { ChannelOptionsDialog } from "./ChannelOptionsDialog"
import { useMessages } from "./hooks/useMessages"
import { MessagesAvatar } from "./MessagesAvatar"

type Props = {
  showChannelOptions?: boolean
  showMobileBackButton?: boolean
}

const MessagingChannelUserQuery = graphql(`
  query MessagingChannelUser($userId: ID!) {
    user(id: $userId) {
      id
      creative {
        id
        __typename
        ... on IndividualCreative {
          id
          handle
          displayName
          professionString
        }
      }
      client {
        id
        __typename
        ... on IndividualClient {
          handle
          jobTitle
        }
      }
    }
  }
`)

const MessagingChannelJobPostingQuery = graphql(`
  query MessagingChannelJobPosting(
    $jobPostingId: ID!
    $skipCreativeJobPosting: Boolean = true
    $skipClientJobPosting: Boolean = true
  ) {
    creativeJobPosting(id: $jobPostingId) @skip(if: $skipCreativeJobPosting) {
      id
      title
    }
    clientJobPosting(id: $jobPostingId) @skip(if: $skipClientJobPosting) {
      id
      title
    }
  }
`)

export const MessagingChannelHeader = (props: Props) => {
  const { showChannelOptions = true, showMobileBackButton = true } = props
  const { channel } = useChannelStateContext()
  const { toggleMobileChannel } = useMessages()
  const { mutedChannels, setMutedChannels } = useMessages()
  const session = useSession()
  const router = useRouter()

  // @ts-ignore
  const jobPostingId = `${channel?.data?.job_posting_id}`
  const sessionUuid = session?.data?.user?.uuid

  // FIXME: need to add custom fields to channel data
  const [archived, setArchived] = useState(Boolean(channel?.data?.hidden))
  const isMuted = mutedChannels[channel?.id!]

  const members = useMemo(
    () => Object.values(channel.state.members || {}).filter(member => member.user?.id !== sessionUuid),
    [channel.state.members, sessionUuid]
  )

  const member = members[0]
  const userId = `${member?.user?.relay_id}`

  // @ts-ignore
  const topic = channel?.data?.topic

  const { data: userData, loading: userLoading } = useQuery(MessagingChannelUserQuery, {
    variables: { userId },
    skip: !userId,
  })

  const { data: jobPostingData, loading: jobPostingLoading } = useQuery(MessagingChannelJobPostingQuery, {
    variables: {
      jobPostingId,
      skipCreativeJobPosting: Boolean(channel?.type !== "creative_job"),
      skipClientJobPosting: Boolean(channel?.type !== "client_job"),
    },
    skip: !jobPostingId, // Only fetch if jobPostingId is available
  })

  const jobPosting = jobPostingData?.creativeJobPosting || jobPostingData?.clientJobPosting
  const subText = userData?.user?.creative?.professionString || userData?.user?.client?.jobTitle
  const memberIsClient = member?.channel_role === "client_channel_member"

  const profileLink = useMemo(() => {
    if (memberIsClient) {
      return userData?.user?.client?.handle ? `/clients/${userData?.user?.client?.handle}` : null
    } else {
      return userData?.user?.creative?.handle ? `/${userData?.user?.creative?.handle}` : null
    }
  }, [memberIsClient, userData])

  return (
    <>
      <div className="flex min-h-[90px] flex-shrink-0 flex-row items-center justify-between border-b border-bg-3 bg-bg-1 px-7 md:bg-bg-1">
        <div className="flex flex-row items-center">
          {showMobileBackButton && (
            <div className="pr-4 md:hidden">
              <ChevronLeftIcon height={24} width={24} onClick={toggleMobileChannel} />
            </div>
          )}
          <div
            onClick={() => (profileLink ? router.push(profileLink) : null)}
            className={clsx("flex flex-row items-center", profileLink && "cursor-pointer")}>
            <div className="pr-4">
              <MessagesAvatar
                src={(members?.[0]?.user?.image as string) ?? ""}
                alt={members?.[1]?.user?.name ?? ""}
                rounded
                showClientTag={memberIsClient}
                size="medium"
              />
            </div>
            <div>
              <Text variant="body1">{member?.user?.name || "Unnamed User"}</Text>
              {userLoading ? (
                <div className="mt-1 h-[14px] w-[110px] animate-pulse rounded-sm bg-bg-2" />
              ) : (
                <Text variant="label" className="mt-1 text-fg-2" maxLines={2}>
                  {subText}
                </Text>
              )}
            </div>
          </div>
        </div>
        {showChannelOptions && (
          <ChannelOptionsDialog
            onMute={(mute: boolean) => {
              setMutedChannels({
                [channel.id!]: mute,
              })
            }}
            onArchive={(archive: boolean) => setArchived(archive)}
            isMuted={isMuted}
            isArchived={archived}
            channel={channel}
          />
        )}
      </div>
      {topic && jobPosting && (
        <Link href={`/jobs/${jobPosting.id}`}>
          <div className="flex flex-row items-center justify-center border-b border-bg-3 bg-bg-2 px-4 py-3">
            <div className="mr-4 flex flex-shrink-0">
              <Text>Job chat:</Text>
            </div>
            <Text maxLines={1} className="text-primary-6">
              {jobPosting?.title}
            </Text>
          </div>
        </Link>
      )}
    </>
  )
}
